'use client';
import React, { useEffect, useLayoutEffect, useRef } from 'react';

type GrowEl = {
  style: { height: string; overflowY: string };
  scrollHeight: number;
  offsetHeight?: number;
  clientHeight?: number;
};

/** Size a textarea to its content (no inner scroll). `rows` still sets the minimum height. */
export function autoGrow(el: GrowEl | null): void {
  if (!el) return;
  const previous = el.style.height;
  el.style.height = 'auto';
  // Hidden (display:none) boxes measure 0; leave them alone so they don't collapse.
  if (el.scrollHeight === 0) {
    el.style.height = previous;
    return;
  }
  el.style.overflowY = 'hidden';
  // scrollHeight excludes borders; add them back for border-box sizing.
  const border = Math.max(0, (el.offsetHeight ?? 0) - (el.clientHeight ?? 0));
  el.style.height = `${el.scrollHeight + border}px`;
}

const useIsoLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect;

/** A textarea that grows with its content: resized on mount, on every value change and on window resize. */
export function AutoGrowTextarea(props: React.TextareaHTMLAttributes<HTMLTextAreaElement>) {
  const ref = useRef<HTMLTextAreaElement>(null);
  useIsoLayoutEffect(() => {
    autoGrow(ref.current);
  }, [props.value]);
  useEffect(() => {
    const onResize = () => autoGrow(ref.current);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return <textarea {...props} ref={ref} onInput={(e) => { autoGrow(e.currentTarget); props.onInput?.(e); }} />;
}
